export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  category: "Prevention" | "Healthy living" | "Medicines" | "Mental wellbeing";
  readTime: string;
  published: string;
  imageTone: "aqua" | "coral" | "lime" | "blue";
  featured?: boolean;
  sourceLabel: string;
  sourceUrl: string;
  sections: Array<{
    heading: string;
    paragraphs: string[];
    bullets?: string[];
  }>;
};

export const blogPosts: BlogPost[] = [
  {
    slug: "understanding-blood-pressure",
    title: "Blood pressure: the quiet number worth knowing",
    excerpt:
      "High blood pressure often has no obvious symptoms. Learn what the numbers represent, why repeat checks matter, and how everyday habits fit into long-term care.",
    category: "Prevention",
    readTime: "4 min read",
    published: "2026-10-02",
    imageTone: "aqua",
    featured: true,
    sourceLabel: "World Health Organization — Hypertension",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/hypertension",
    sections: [
      {
        heading: "What a blood pressure reading means",
        paragraphs: [
          "Blood pressure is the force of blood pushing against the walls of your blood vessels. A reading contains two numbers: systolic pressure, measured while the heart contracts, and diastolic pressure, measured while the heart rests between beats. Both numbers help a clinician understand how hard the cardiovascular system is working.",
          "One reading is a snapshot, not a complete story. Activity, stress, caffeine, pain, medicines and measurement technique can all affect the result. Hypertension is assessed from properly taken readings, often on more than one occasion, alongside a person's health history and overall cardiovascular risk.",
        ],
      },
      {
        heading: "Why regular checks matter",
        paragraphs: [
          "Most people with high blood pressure feel no symptoms, which is why it is sometimes called a silent condition. When it remains uncontrolled, it can contribute to heart disease, stroke, kidney damage and other health problems. Measuring it is quick, and knowing the result creates an opportunity for an informed conversation.",
          "Home monitors can help show patterns when they are used correctly, but they do not replace professional assessment. A clinician can check the device and cuff fit, review the measurement method, look for related conditions and explain what the pattern means for that individual.",
        ],
        bullets: [
          "Sit quietly before a measurement and support the arm.",
          "Use a cuff that fits and follow the device instructions.",
          "Record readings with the date and time instead of relying on memory.",
        ],
      },
      {
        heading: "A plan is usually more than one number",
        paragraphs: [
          "Healthy eating with less excess salt, regular movement, avoiding tobacco, moderating alcohol and reaching a sustainable weight can support blood pressure health. Some people also need prescription medicine. Medicines should be taken and reviewed as directed; they should not be shared, stopped or adjusted based only on a single home reading.",
          "A very high reading or symptoms such as chest pain, severe headache, difficulty breathing, confusion or vision changes may require urgent assessment. This article is general education, not a diagnosis or personal treatment plan. A healthcare professional can interpret readings and symptoms in context.",
        ],
      },
    ],
  },
  {
    slug: "building-a-balanced-plate",
    title: "A balanced plate without the perfection pressure",
    excerpt:
      "Healthy eating is a pattern, not a single superfood. Use four flexible principles to make everyday meals more varied, balanced and satisfying.",
    category: "Healthy living",
    readTime: "4 min read",
    published: "2026-09-18",
    imageTone: "lime",
    featured: true,
    sourceLabel: "World Health Organization — Healthy diet",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/healthy-diet",
    sections: [
      {
        heading: "Start with four useful principles",
        paragraphs: [
          "A healthy diet can look different across homes, cultures and budgets. The World Health Organization describes four foundations that travel well: adequacy, balance, moderation and diversity. In everyday language, that means getting enough nutrients, matching energy intake to your needs, limiting foods or ingredients that can be harmful in excess, and choosing a variety of foods.",
          "These ideas are more helpful than labelling individual foods as good or bad. One meal does not determine health. What matters most is the pattern built over time, while allowing room for preferences, traditions, access and enjoyment.",
        ],
      },
      {
        heading: "Make minimally processed foods the regulars",
        paragraphs: [
          "Vegetables, fruits, pulses such as beans and lentils, whole grains, nuts, seeds and suitable sources of protein can form the centre of a varied eating pattern. Fresh produce is not the only option: frozen and canned choices can be useful too, especially when they have little added sugar or excess sodium.",
          "Fibre-rich carbohydrates provide energy and support digestive health. Unsaturated fats found in foods such as fish, avocado, nuts and many plant oils are generally preferable to saturated and trans fats. Water is a simple default drink, while sugary drinks and fruit juice can add free sugars quickly.",
        ],
        bullets: [
          "Add one colourful plant food to a familiar meal.",
          "Choose whole-grain or higher-fibre options when practical.",
          "Compare labels for sodium, added sugars and saturated fat.",
          "Use herbs, spices, citrus and aromatics to build flavour.",
        ],
      },
      {
        heading: "Small changes can be more durable",
        paragraphs: [
          "A realistic next step might be adding vegetables to lunch, cooking one more meal at home, or replacing a frequent sugary drink with water. Changes that fit daily life are easier to repeat than a strict plan that depends on constant willpower. Food safety, affordability and adequate nutrition still matter.",
          "Needs can differ with age, pregnancy, activity, allergies, culture and medical conditions. This article is educational and cannot diagnose a nutrient deficiency or prescribe a diet. A qualified clinician or dietitian can help tailor choices, especially when kidney disease, diabetes, digestive conditions or disordered eating are part of the picture.",
        ],
      },
    ],
  },
  {
    slug: "movement-that-fits-real-life",
    title: "Movement that fits real life",
    excerpt:
      "Exercise does not have to arrive in one long workout. Explore how walking, strength, balance and short activity breaks can add up across the week.",
    category: "Healthy living",
    readTime: "4 min read",
    published: "2026-09-04",
    imageTone: "coral",
    sourceLabel: "Centers for Disease Control and Prevention — Physical Activity Basics",
    sourceUrl: "https://www.cdc.gov/physical-activity-basics/guidelines/index.html",
    sections: [
      {
        heading: "Some activity is better than none",
        paragraphs: [
          "Regular physical activity supports the heart, muscles, bones, mood and day-to-day function. For adults, public-health guidance commonly describes at least 150 minutes of moderate-intensity aerobic activity across a week, together with muscle-strengthening activity on at least two days. Older adults also benefit from activities that challenge balance.",
          "That target is a direction, not an entry requirement. Someone who is currently inactive can begin below it. Short walks, active errands, gardening, dancing or standing and moving between periods of sitting all count toward a more active pattern.",
        ],
      },
      {
        heading: "Use the talk test and build gradually",
        paragraphs: [
          "During moderate-intensity activity, breathing and heart rate increase, but most people can still talk. Vigorous activity feels harder and makes conversation more difficult. Fitness level changes how an activity feels, so the same walking pace may be easy for one person and challenging for another.",
          "Progress can come from adding a few minutes, another day or a little more challenge—not all three at once. Strength work can use weights, resistance bands, machines or body weight. Recovery time, comfortable footwear and a suitable environment can make a routine easier to sustain.",
        ],
        bullets: [
          "Choose an activity you are willing to repeat.",
          "Attach movement to an existing routine, such as after lunch.",
          "Track consistency before speed, distance or calories.",
          "Include mobility and balance where they support daily function.",
        ],
      },
      {
        heading: "Adapt movement to the person",
        paragraphs: [
          "Age, pregnancy, disability, chronic conditions, recent surgery and current symptoms can change what is appropriate. If the full guideline is not possible, being as active as abilities and circumstances allow can still be meaningful. A healthcare or exercise professional can help modify activities safely.",
          "New chest discomfort, fainting, severe breathlessness or other concerning symptoms during activity deserve prompt medical assessment. This article offers general education only; it is not an exercise prescription or a diagnosis.",
        ],
      },
    ],
  },
  {
    slug: "better-sleep-starts-before-bed",
    title: "Better sleep starts before bedtime",
    excerpt:
      "Sleep is both quantity and quality. A steady rhythm, a calmer room and a few daytime habits can help create better conditions for rest.",
    category: "Healthy living",
    readTime: "4 min read",
    published: "2026-08-21",
    imageTone: "blue",
    sourceLabel: "Centers for Disease Control and Prevention — About Sleep",
    sourceUrl: "https://www.cdc.gov/sleep/about/",
    sections: [
      {
        heading: "Sleep is active maintenance",
        paragraphs: [
          "Sleep supports attention, memory, emotional wellbeing, metabolism and heart health. Healthy sleep is not only about hours; quality and regularity matter too. The amount people need changes with age, and individual needs vary, but most adults require a substantial nightly period of sleep rather than trying to catch up occasionally.",
          "A difficult night is common and does not automatically signal a disorder. The more useful questions are whether sleep problems persist, whether they affect daytime functioning, and whether symptoms such as loud snoring, pauses in breathing or overwhelming sleepiness are present.",
        ],
      },
      {
        heading: "Give the body consistent cues",
        paragraphs: [
          "The body clock responds to patterns. Keeping wake and sleep times reasonably consistent can make those cues clearer. A bedroom that is quiet, relaxing and comfortably cool can support rest, while bright screens, large late meals, alcohol and late caffeine can interfere for some people.",
          "Daytime habits matter as well. Regular movement, exposure to daylight and a balanced diet help connect activity with daytime and rest with nighttime. A brief wind-down routine—dim lights, quiet reading, gentle stretching or another calming activity—can mark the transition without turning sleep into a performance test.",
        ],
        bullets: [
          "Keep a stable wake time when possible.",
          "Reduce electronic-device use shortly before bed.",
          "Notice when caffeine affects you and adjust timing.",
          "Write down persistent patterns to discuss with a clinician.",
        ],
      },
      {
        heading: "When sleep needs a closer look",
        paragraphs: [
          "Ongoing insomnia, breathing interruptions, restless sensations, nightmares or significant daytime sleepiness can have many causes. Medicines, pain, mood, work schedules and other medical conditions may be involved. A professional assessment can look beyond generic sleep tips and decide whether testing or treatment is appropriate.",
          "Avoid driving or operating equipment when dangerously sleepy. This article is for general education and is not a diagnosis or individualized sleep treatment. A healthcare professional can help when sleep problems are frequent, distressing or affecting safety.",
        ],
      },
    ],
  },
  {
    slug: "vaccines-how-immune-memory-works",
    title: "Vaccines and immune memory, explained simply",
    excerpt:
      "Vaccination prepares the immune system before exposure to a disease. Here is how that memory works and why recommendations differ from person to person.",
    category: "Prevention",
    readTime: "4 min read",
    published: "2026-08-07",
    imageTone: "aqua",
    sourceLabel: "World Health Organization — Vaccines and immunization",
    sourceUrl:
      "https://www.who.int/news-room/questions-and-answers/item/vaccines-and-immunization-what-is-vaccination",
    sections: [
      {
        heading: "Training before the real encounter",
        paragraphs: [
          "Vaccination introduces the immune system to a safe representation of a germ or part of it. The immune system responds by producing antibodies and other protective responses, then remembers what it learned. If the real germ appears later, that memory can help the body respond more quickly.",
          "Vaccines are delivered in different ways, including injection, by mouth or through the nose. Some require more than one dose or later boosters to build or refresh protection. The exact schedule depends on the vaccine, age, health history, previous doses, local guidance and sometimes travel or occupation.",
        ],
      },
      {
        heading: "Protection is personal and shared",
        paragraphs: [
          "Vaccination lowers the risk of illness and serious complications from many infections. It can also reduce opportunities for some infections to spread, helping protect people who are more vulnerable or cannot receive a particular vaccine. No medical intervention is completely risk-free, but vaccines are studied and monitored for safety.",
          "Expected short-lived reactions can include soreness at the injection site, tiredness or a mild fever, depending on the vaccine. Serious reactions are uncommon. Healthcare professionals can explain known benefits, common reactions and specific precautions in language that supports an informed decision.",
        ],
        bullets: [
          "Keep a record of doses and bring it to healthcare visits.",
          "Ask about missed doses rather than assuming a series must restart.",
          "Mention severe allergies, immune conditions, pregnancy and current illness.",
          "Use official health sources when checking vaccine claims.",
        ],
      },
      {
        heading: "Why one schedule does not fit everyone",
        paragraphs: [
          "Recommendations vary among countries and can change as evidence and disease patterns evolve. Certain immune-suppressing treatments, rare severe allergies or acute illness may alter the timing or choice of a vaccine. A clinician can compare a person's record with current guidance where they live.",
          "This article provides general education only. It does not determine which vaccine an individual should receive, diagnose a reaction or replace advice from a qualified healthcare professional or local public-health authority.",
        ],
      },
    ],
  },
  {
    slug: "type-2-diabetes-beyond-sugar",
    title: "Type 2 diabetes is about more than sugar",
    excerpt:
      "Understand insulin, gradual symptoms and the role of screening—without blame, shortcuts or one-size-fits-all advice.",
    category: "Prevention",
    readTime: "4 min read",
    published: "2026-07-24",
    imageTone: "coral",
    sourceLabel: "World Health Organization — Diabetes",
    sourceUrl: "https://www.who.int/news-room/questions-and-answers/item/diabetes",
    sections: [
      {
        heading: "The role of insulin",
        paragraphs: [
          "Glucose is an important fuel, and insulin helps move it from the bloodstream into cells. In type 2 diabetes, the body does not use insulin effectively and may gradually stop making enough. Blood glucose can then remain too high. Over time, this can affect blood vessels, nerves, the heart, kidneys, eyes and feet.",
          "Type 2 diabetes develops through a mix of genetics, ageing, environment and lifestyle factors. It is not a simple measure of effort or character. Avoiding blame makes it easier to focus on useful steps: understanding risk, arranging appropriate testing and building a care plan that can be maintained.",
        ],
      },
      {
        heading: "Symptoms can be subtle or absent",
        paragraphs: [
          "Possible symptoms include increased thirst or hunger, frequent urination, marked tiredness, blurred vision and unexplained weight loss. However, type 2 diabetes may cause mild symptoms or none for years. Symptoms alone therefore cannot confirm or exclude it.",
          "Diagnosis uses blood tests such as fasting plasma glucose, an oral glucose tolerance test, HbA1c or, in selected circumstances, random plasma glucose. Each test has defined thresholds and limitations. A healthcare professional chooses and interprets testing in the clinical context rather than relying on a home device alone.",
        ],
        bullets: [
          "Know whether diabetes runs in your family.",
          "Bring previous blood-test results to routine appointments.",
          "Mention a history of diabetes during pregnancy.",
          "Discuss persistent symptoms rather than self-diagnosing online.",
        ],
      },
      {
        heading: "Risk reduction and care are individualized",
        paragraphs: [
          "Regular physical activity, a varied diet, avoiding tobacco and reaching a sustainable weight can reduce risk and support management. People who develop diabetes may also need medicines and monitoring for related health issues. The right combination depends on the person and can change over time.",
          "Sudden or severe illness with symptoms of very high blood glucose requires prompt assessment. This article is general education, not a diagnosis, screening recommendation or treatment plan. A clinician can assess personal risk and explain whether testing is appropriate.",
        ],
      },
    ],
  },
  {
    slug: "colorectal-screening-informed-conversation",
    title: "Colorectal screening: start with an informed conversation",
    excerpt:
      "Screening can find colorectal cancer before symptoms appear, but the right test and timing depend on personal risk and local programmes.",
    category: "Prevention",
    readTime: "4 min read",
    published: "2026-07-10",
    imageTone: "blue",
    sourceLabel: "World Health Organization — Colorectal cancer",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/colorectal-cancer",
    sections: [
      {
        heading: "Screening and symptom checks are different",
        paragraphs: [
          "Screening looks for possible disease or precancerous changes in people who do not have symptoms. Depending on the programme, it may use a stool-based test, an examination of the colon or another method. An abnormal screening result is not itself a cancer diagnosis; it means follow-up may be needed.",
          "Symptoms require a clinical assessment rather than waiting for routine screening. Possible warning signs include blood in the stool, a persistent change in bowel habits, abdominal pain, unexplained weight loss, fatigue or low iron levels. These symptoms can have many causes, but they deserve appropriate evaluation.",
        ],
      },
      {
        heading: "Risk is not determined by age alone",
        paragraphs: [
          "Colorectal cancer becomes more common with age, yet it can occur in younger adults. Family history, inherited conditions, previous colorectal cancer or certain polyps may increase risk. Lifestyle factors associated with risk include tobacco, alcohol, physical inactivity, excess body weight and eating patterns high in processed or red meat and low in fruits and vegetables.",
          "No single habit guarantees prevention. A pattern that includes regular movement, avoiding tobacco and a diverse, fibre-containing diet can support overall health, while screening aims to find changes earlier. The two approaches complement each other rather than compete.",
        ],
        bullets: [
          "Learn whether close relatives have had colorectal cancer or polyps.",
          "Do not dismiss persistent bowel changes because of age.",
          "Ask what each available test can and cannot show.",
          "Clarify what follow-up would happen after an abnormal result.",
        ],
      },
      {
        heading: "Timing should be personalized",
        paragraphs: [
          "Screening programmes, available tests and recommended starting points differ by country and health system. Personal and family history may also justify a different approach. That is why a conversation with a qualified clinician is more useful than applying a schedule found online to everyone.",
          "This article is for education only. It does not diagnose symptoms or provide a personal screening schedule. A healthcare professional can review risk factors, local options, benefits and possible harms and help with an informed choice.",
        ],
      },
    ],
  },
  {
    slug: "antibiotics-when-they-help",
    title: "Antibiotics: when they help—and when they do not",
    excerpt:
      "Antibiotics target certain bacterial infections, not viruses. Thoughtful use protects patients today and keeps these medicines useful for the future.",
    category: "Medicines",
    readTime: "4 min read",
    published: "2026-06-26",
    imageTone: "lime",
    sourceLabel: "Centers for Disease Control and Prevention — Antibiotic use",
    sourceUrl: "https://www.cdc.gov/antibiotic-use/about/index.html",
    sections: [
      {
        heading: "Different germs need different tools",
        paragraphs: [
          "Antibiotics treat certain infections caused by bacteria. They do not work against viruses such as those responsible for colds or influenza, and many sore throats and cases of bronchitis are viral. Even some bacterial infections can improve without antibiotics, depending on the illness and the person.",
          "Symptoms and mucus colour alone do not reliably reveal which germ is responsible. A clinician considers the pattern of illness, examination findings, risk factors and sometimes testing before deciding whether an antibiotic is likely to help.",
        ],
      },
      {
        heading: "Unnecessary use has real costs",
        paragraphs: [
          "When antibiotics are not needed, they cannot speed recovery from a viral infection. They can still cause side effects, including nausea, diarrhoea, rash, yeast infection and, less commonly, serious allergic reactions or other complications. Antibiotic exposure also contributes to antimicrobial resistance, in which germs become harder to treat.",
          "Resistance does not mean a person's body has become resistant. It means bacteria or other microbes have changed. Resistant infections can spread between people, so appropriate antibiotic use is both an individual safety issue and a shared public-health responsibility.",
        ],
        bullets: [
          "Take an antibiotic exactly as prescribed for you.",
          "Do not save leftovers or share them with someone else.",
          "Ask what the medicine is treating and what side effects to watch for.",
          "Use a medicine take-back option or ask a pharmacist about disposal.",
        ],
      },
      {
        heading: "Good care is not always a prescription",
        paragraphs: [
          "When an antibiotic is not appropriate, a clinician can still discuss symptom relief, expected recovery and signs that the illness should be reassessed. Hand hygiene, covering coughs, staying away from others when unwell and recommended vaccination can help reduce infections and their spread.",
          "This article is general education, not a diagnosis or a recommendation to start, stop or select an antibiotic. New or worsening symptoms and possible medicine reactions should be assessed by a qualified healthcare professional.",
        ],
      },
    ],
  },
  {
    slug: "quitting-tobacco-plan-not-test",
    title: "Quitting tobacco is a plan, not a test of willpower",
    excerpt:
      "Nicotine dependence is powerful, and support matters. Learn how triggers, practical preparation and evidence-based help can make another quit attempt worthwhile.",
    category: "Prevention",
    readTime: "4 min read",
    published: "2026-06-12",
    imageTone: "coral",
    sourceLabel: "World Health Organization — Tobacco and nicotine",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/tobacco",
    sections: [
      {
        heading: "Dependence is not a character flaw",
        paragraphs: [
          "Nicotine is highly addictive. Tobacco use becomes connected not only to physical dependence but also to routines, emotions and social cues. That combination helps explain why wanting to quit and finding it difficult can both be true. A return to tobacco after a quit attempt is information for the next plan, not proof that change is impossible.",
          "All forms of tobacco are harmful, and second-hand smoke can also harm people nearby. Heated tobacco and smokeless products are not harmless alternatives. Product marketing can blur those facts, so health decisions are better grounded in independent public-health information.",
        ],
      },
      {
        heading: "Make triggers visible",
        paragraphs: [
          "For several days, noticing when tobacco is used can reveal patterns: after meals, during stress, with certain drinks, while driving or around particular people. Each trigger can have a prepared response, such as changing the routine, taking a short walk, contacting a support person or using another coping strategy.",
          "Removing tobacco products and reminders from the environment can reduce automatic use. Telling trusted people what kind of support is welcome can make the plan more concrete. Some people prefer a set quit date; others work with a clinician on a structured reduction and treatment plan.",
        ],
        bullets: [
          "Write down personal reasons for quitting.",
          "Identify high-risk moments and an alternative for each.",
          "Plan for cravings without judging them.",
          "Seek professional support after a setback rather than abandoning the goal.",
        ],
      },
      {
        heading: "Support can improve the process",
        paragraphs: [
          "Behavioural support and approved stop-smoking medicines can help, and they may be used together for some people. The suitable option depends on health history, pregnancy, other medicines, the tobacco product used and local availability. A qualified professional can explain benefits, side effects and correct use.",
          "This article provides education, not a personal cessation prescription. A clinician or trained cessation service can help create an individualized plan and address mood changes, withdrawal or other concerns during quitting.",
        ],
      },
    ],
  },
  {
    slug: "medication-list-that-travels-with-you",
    title: "The medication list that travels with you",
    excerpt:
      "One current list can help clinicians and pharmacists see the full picture—from prescriptions to vitamins, allergies and recent dose changes.",
    category: "Medicines",
    readTime: "4 min read",
    published: "2026-05-29",
    imageTone: "aqua",
    sourceLabel: "U.S. Food and Drug Administration — Keep a medication list",
    sourceUrl:
      "https://www.fda.gov/consumers/consumer-updates/create-and-keep-medication-list-your-health",
    sections: [
      {
        heading: "Why one list can prevent confusion",
        paragraphs: [
          "A medication list brings important information into one place when clinics, pharmacies and laboratories may use separate systems. It can help healthcare professionals spot duplications, interactions and medicines that may affect tests. It is especially useful when a person is tired, in pain or unable to remember every detail.",
          "The list should include more than prescription medicines. Over-the-counter products, vitamins, minerals, herbal products and other supplements can also interact with treatment or matter during a procedure. Allergies and the reaction they caused belong on the record too.",
        ],
      },
      {
        heading: "Record the details that answer questions",
        paragraphs: [
          "For each item, write the name, strength, dose, timing, method of use and the reason it is taken. Include medicines used only when needed, injections, inhalers, creams, patches and eye drops. A photo of the label can help, but a clear list is faster to review and easier to update.",
          "Paper, a phone note, an app or a printed template can all work. The best format is one that is secure, easy to maintain and available during appointments. A trusted caregiver may keep a copy when appropriate and with the person's consent.",
        ],
        bullets: [
          "Prescription and nonprescription medicines",
          "Vitamins, minerals, herbs and supplements",
          "Dose, timing, purpose and prescribing clinician",
          "Allergies, previous reactions and relevant conditions",
        ],
      },
      {
        heading: "Keep it current and share it",
        paragraphs: [
          "Update the list after a new prescription, dose change or stopped medicine. At a visit, compare it with the clinic's record and ask about differences. A pharmacist can also help review names, strengths and possible duplication. Keep privacy in mind when storing or sharing the document electronically.",
          "A list improves communication but cannot decide whether a medicine is still needed. Do not stop, restart or change a dose from the list alone. This article is educational, not a medication review; prescribing questions and suspected side effects should be discussed with a qualified healthcare professional.",
        ],
      },
    ],
  },
  {
    slug: "depression-deserves-care",
    title: "Depression deserves care, not judgment",
    excerpt:
      "Depression is more than a difficult day. Recognizing persistent changes in mood, interest, sleep and functioning can open the door to effective support.",
    category: "Mental wellbeing",
    readTime: "4 min read",
    published: "2026-05-15",
    imageTone: "blue",
    featured: true,
    sourceLabel: "World Health Organization — Depressive disorder",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/depression",
    sections: [
      {
        heading: "Different from ordinary mood changes",
        paragraphs: [
          "Everyone experiences sadness, disappointment or low motivation. Depression is different: it involves a depressed mood or loss of interest or pleasure that persists and affects daily life. It can influence relationships, work, study, sleep, appetite, concentration and energy. People may also experience guilt, low self-worth or hopelessness.",
          "Depression can affect anyone. Social stress, loss, trauma, physical illness and biological factors may interact, and sometimes there is no single clear cause. It is not weakness, laziness or something a person should simply be able to think away.",
        ],
      },
      {
        heading: "A conversation can be a first step",
        paragraphs: [
          "If changes persist or make usual activities difficult, speaking with a healthcare professional can help. Assessment considers symptoms, duration, functioning, physical health, medicines and other possible explanations. That context matters because grief, bipolar disorder, thyroid conditions, substance use and other issues may require a different approach.",
          "Support can include psychological treatments, social support, practical changes and, for some people, medicine. Effective options exist across mild, moderate and severe depression, but the right plan is individual. Recovery is not always linear, and follow-up allows care to be adjusted.",
        ],
        bullets: [
          "Listen without minimizing or rushing to fix the feeling.",
          "Offer practical company for an appointment if wanted.",
          "Keep invitations gentle and specific.",
          "Take talk of death, self-harm or suicide seriously.",
        ],
      },
      {
        heading: "Safety comes first",
        paragraphs: [
          "Thoughts of self-harm or suicide, an inability to stay safe, or severe changes in behaviour need immediate support through local urgent health or crisis services. Stay with the person when it is safe to do so, involve a trusted support person and reduce access to obvious means of harm while professional help is arranged.",
          "This article is general education and cannot diagnose depression or replace urgent or ongoing mental healthcare. A qualified professional can assess symptoms, safety and treatment needs. If this topic feels personally relevant, reaching out is a health action—not a failure.",
        ],
      },
    ],
  },
  {
    slug: "heat-health-plan-ahead",
    title: "Heat and health: plan before the temperature peaks",
    excerpt:
      "Hot days can strain the body, especially when nights stay warm. Learn who is more vulnerable and how preparation can reduce exposure.",
    category: "Healthy living",
    readTime: "4 min read",
    published: "2026-04-24",
    imageTone: "coral",
    sourceLabel: "World Health Organization — Heat and health",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/climate-change-heat-and-health",
    sections: [
      {
        heading: "Heat affects more than comfort",
        paragraphs: [
          "During unusually hot days and nights, the body works harder to keep its internal temperature stable. Heat can worsen cardiovascular, respiratory, kidney, metabolic and mental-health conditions. It may also increase fatigue, reduce concentration and raise the chance of accidents at work, during travel or while exercising.",
          "Risk depends on temperature, humidity, duration, activity, housing and access to cooling. Older adults, infants, pregnant people, outdoor workers, people living alone and those with some chronic conditions or medicines may be more vulnerable. A person can be at risk even if they do not feel extremely thirsty.",
        ],
      },
      {
        heading: "Reduce exposure before symptoms begin",
        paragraphs: [
          "Official weather and heat alerts can guide plans. When possible, strenuous tasks can be moved away from the hottest part of the day, and time can be spent in shade or a cooler place. Light, loose clothing and regular access to water also support the body's cooling processes.",
          "Homes may stay cooler when direct sun is blocked during the day and cooler night air is used when conditions allow. Fans are not enough in every temperature or for every person, so local public-health guidance matters. Checking on someone who is older, unwell or living alone can reveal problems early.",
        ],
        bullets: [
          "Review the forecast and official heat alerts.",
          "Plan water, shade, rest and transport before going out.",
          "Never leave a person or animal in a parked vehicle.",
          "Ask a clinician whether illness or medicines change heat risk.",
        ],
      },
      {
        heading: "Know when heat becomes urgent",
        paragraphs: [
          "Heavy sweating, weakness, headache, dizziness, nausea or cramps can signal heat illness. Confusion, collapse, seizures or very hot skin can indicate a medical emergency. Rapid cooling and urgent professional assessment are important; follow the emergency-care guidance where you are.",
          "This article is general education, not a diagnosis or a personal fluid plan. Some people with heart or kidney conditions have prescribed fluid limits, so generic hydration targets may be unsafe for them. A healthcare professional can provide advice based on health conditions and medicines.",
        ],
      },
    ],
  },
  {
    slug: "when-worry-becomes-more-than-stress",
    title: "When worry becomes more than everyday stress",
    excerpt:
      "Anxiety is a normal human response, but persistent fear or worry can begin to narrow daily life. Learn what patterns deserve a closer conversation.",
    category: "Mental wellbeing",
    readTime: "4 min read",
    published: "2026-10-03",
    imageTone: "blue",
    sourceLabel: "World Health Organization — Anxiety disorders",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/anxiety-disorders",
    sections: [
      {
        heading: "Anxiety can be useful—and still become overwhelming",
        paragraphs: [
          "Anxiety helps the body prepare for challenge or danger. Before an interview, examination or unfamiliar event, temporary worry and physical tension can be understandable. An anxiety disorder is different: fear or worry becomes intense, difficult to control, lasts over time and causes significant distress or interferes with home, work, study or relationships.",
          "The experience is not only in a person's thoughts. It may include restlessness, irritability, poor concentration, sleep difficulty, nausea, sweating, trembling, a racing heart or a sense of impending danger. These symptoms can also occur with physical conditions, medicines or substances, so a symptom list alone cannot establish the cause.",
        ],
      },
      {
        heading: "Patterns matter more than labels",
        paragraphs: [
          "Anxiety disorders take different forms. Worry may spread across many parts of life, cluster around social situations, occur as sudden panic attacks or focus on a particular object or setting. Avoidance can bring short-term relief while gradually making work, travel, healthcare or relationships more difficult.",
          "Effective care is available. Depending on the person, it may include psychological treatment, support for related stresses and sometimes medicine. Cognitive behavioural approaches have strong evidence and can help people understand anxiety patterns and face feared situations gradually with appropriate support. Preferences, other health conditions and access all shape the plan.",
        ],
        bullets: [
          "Notice how long worry lasts and what it interrupts.",
          "Record physical symptoms, sleep changes and possible triggers.",
          "Consider whether avoidance is making life smaller.",
          "Bring medicine, caffeine, alcohol and substance use into the conversation.",
        ],
      },
      {
        heading: "Reaching out is a practical health step",
        paragraphs: [
          "A healthcare or mental-health professional can explore the pattern, its effect on daily life and possible physical contributors. Thoughts of self-harm, an inability to stay safe or a severe and rapidly worsening change require immediate support through local urgent health or crisis services.",
          "This article is general education, not a diagnosis or personal treatment plan. It cannot determine whether symptoms are caused by an anxiety disorder or another condition. A qualified professional can provide an individualized assessment and discuss suitable support.",
        ],
      },
    ],
  },
  {
    slug: "asthma-symptoms-triggers-action-plan",
    title: "Asthma: symptoms, triggers and the value of an action plan",
    excerpt:
      "Asthma symptoms can come and go, but airway inflammation may still need attention. Understand common patterns, triggers and the purpose of a personal action plan.",
    category: "Prevention",
    readTime: "4 min read",
    published: "2026-09-26",
    imageTone: "aqua",
    sourceLabel: "World Health Organization — Asthma",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/asthma",
    sections: [
      {
        heading: "What changes inside the airways",
        paragraphs: [
          "Asthma is a chronic lung condition in which inflammation and tightening around the small airways make breathing harder. Symptoms can include cough, wheeze, shortness of breath and chest tightness. They may be mild or severe, vary from day to day and become more noticeable at night, during exercise or with a respiratory infection.",
          "Other conditions can cause similar symptoms, so wheeze or breathlessness does not confirm asthma by itself. A clinician may consider the history, examination and breathing tests, including how airflow changes over time or after treatment, before explaining what the pattern is likely to mean.",
        ],
      },
      {
        heading: "Good control combines medicine, technique and context",
        paragraphs: [
          "Inhaled medicines deliver treatment directly to the lungs. Some open the airways to relieve symptoms, while inhaled corticosteroids reduce airway inflammation and the risk of severe attacks. The appropriate combination depends on the individual. Correct inhaler technique matters because even a useful medicine cannot work well if little reaches the lungs.",
          "Triggers differ between people and may include tobacco smoke, air pollution, dust, mould, pollen, animals, strong scents, workplace exposures, weather changes or infections. The goal is not to fear every possible trigger, but to identify a repeatable pattern and reduce avoidable exposure where practical.",
        ],
        bullets: [
          "Ask a professional to watch and check inhaler technique.",
          "Keep track of night symptoms and activity limitations.",
          "Notice repeated links between symptoms and exposures.",
          "Keep an individualized written asthma action plan accessible.",
        ],
      },
      {
        heading: "An action plan makes changes easier to recognize",
        paragraphs: [
          "A personal asthma action plan explains usual treatment, signs that control is worsening and what to do at each stage. Severe breathlessness, difficulty speaking, marked drowsiness or symptoms that are not responding as expected may require urgent medical assessment through local services.",
          "This guide is educational only. It does not diagnose asthma, select an inhaler or replace a personal action plan. Medicines should not be started, stopped or adjusted from this article; a qualified healthcare professional can assess symptoms and provide individualized instructions.",
        ],
      },
    ],
  },
  {
    slug: "kidney-disease-can-be-silent",
    title: "Kidney disease can be silent: what testing can reveal",
    excerpt:
      "Kidneys do far more than make urine, and early chronic kidney disease may cause no symptoms. Learn how risk and simple tests fit together.",
    category: "Prevention",
    readTime: "4 min read",
    published: "2026-09-12",
    imageTone: "lime",
    sourceLabel: "Centers for Disease Control and Prevention — Chronic Kidney Disease Basics",
    sourceUrl: "https://www.cdc.gov/kidney-disease/about/index.html",
    sections: [
      {
        heading: "Small organs with several essential jobs",
        paragraphs: [
          "The kidneys filter waste and extra fluid from the blood. They also help regulate blood pressure and blood chemistry, support red blood cell production and contribute to bone health. Chronic kidney disease, or CKD, develops when kidney damage or reduced function persists over time.",
          "Early CKD commonly causes no obvious symptoms. Swelling, tiredness, appetite changes or changes in urination may appear later, but these are not specific to kidney disease. Feeling well therefore does not always reflect kidney function, and symptoms alone cannot confirm or exclude a problem.",
        ],
      },
      {
        heading: "Blood and urine answer different questions",
        paragraphs: [
          "A blood creatinine result can be used to estimate how effectively the kidneys filter, often reported as an estimated glomerular filtration rate. A urine test can look for albumin or other protein, which may signal kidney damage. Results need context, because hydration, acute illness, muscle mass and other factors can affect interpretation.",
          "Diabetes and high blood pressure are important CKD risk factors. Heart disease, a family history of kidney disease, previous kidney injury and some medicines may also matter. A clinician can decide whether testing is appropriate and whether an unexpected result should be repeated or investigated further.",
        ],
        bullets: [
          "Know whether kidney disease runs in the family.",
          "Bring previous blood and urine results to appointments.",
          "Include nonprescription medicines and supplements on the medication list.",
          "Ask what a result means over time, not only whether it is flagged.",
        ],
      },
      {
        heading: "Kidney care connects with whole-person care",
        paragraphs: [
          "Managing blood pressure, blood glucose and cardiovascular risk can help protect kidney health. Medicine choices, nutrition and fluid needs can change with kidney function, which is why generic online restrictions or supplement advice may be unsuitable. Early recognition creates more opportunity to address causes and slow progression.",
          "This article is general education, not a diagnosis, screening schedule or kidney-care plan. It cannot interpret an individual laboratory result. A qualified healthcare professional can review personal risk, repeat testing when appropriate and explain the next step.",
        ],
      },
    ],
  },
  {
    slug: "healthy-mouth-healthy-life",
    title: "A healthy mouth supports a healthy life",
    excerpt:
      "Oral health affects comfort, eating, speaking and confidence. A few consistent habits can help prevent common problems and reveal when care is needed.",
    category: "Healthy living",
    readTime: "4 min read",
    published: "2026-08-29",
    imageTone: "coral",
    sourceLabel: "World Health Organization — Oral health",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/oral-health",
    sections: [
      {
        heading: "How common oral problems develop",
        paragraphs: [
          "Tooth decay begins when bacteria in dental plaque use free sugars from food and drinks and produce acids that gradually damage the tooth. Frequent exposure, inadequate fluoride and plaque that is not removed allow the process to continue. Decay can eventually cause sensitivity, pain, infection and tooth loss.",
          "Gum disease affects the tissues that support the teeth. Early signs can include red, swollen or bleeding gums and persistent bad breath. More advanced disease can damage supporting bone and loosen teeth. Tobacco use increases risk, and diabetes and gum disease can influence each other.",
        ],
      },
      {
        heading: "Prevention is a pattern, not a perfect routine",
        paragraphs: [
          "Brushing twice daily with fluoride toothpaste helps remove plaque and strengthens teeth against acid. Cleaning between teeth may reach areas a toothbrush misses. After brushing, following the toothpaste instructions and avoiding immediately washing away the fluoride can help it remain in contact with teeth.",
          "Reducing how often free sugars are consumed, choosing water as a regular drink, avoiding tobacco and moderating alcohol support oral and wider health. Mouthguards and other appropriate protective equipment can reduce dental injury during contact sports and some activities.",
        ],
        bullets: [
          "Use a fluoride toothpaste suitable for the person's age.",
          "Replace a worn toothbrush or brush head.",
          "Notice recurring bleeding rather than accepting it as normal.",
          "Keep regular dental care based on individual need and local access.",
        ],
      },
      {
        heading: "Do not wait for severe pain",
        paragraphs: [
          "Dental pain, facial swelling, a broken tooth, loose adult teeth, persistent bleeding or a mouth ulcer or patch that does not heal deserves professional assessment. Sudden spreading swelling, difficulty breathing or difficulty swallowing may require urgent evaluation through local services.",
          "This article provides general education and cannot diagnose tooth decay, gum disease, infection or oral cancer. It is not a substitute for a dental examination or an individualized hygiene plan. A qualified dental professional can assess symptoms and personal risk.",
        ],
      },
    ],
  },
  {
    slug: "sun-protection-beyond-hot-days",
    title: "Sun protection is about UV—not only hot days",
    excerpt:
      "Ultraviolet radiation can reach skin on cool or cloudy days. Layering simple protections lowers exposure without giving up time outdoors.",
    category: "Prevention",
    readTime: "4 min read",
    published: "2026-08-15",
    imageTone: "aqua",
    sourceLabel: "Centers for Disease Control and Prevention — Reducing Risk for Skin Cancer",
    sourceUrl: "https://www.cdc.gov/skin-cancer/prevention/index.html",
    sections: [
      {
        heading: "Temperature and ultraviolet exposure are different",
        paragraphs: [
          "Ultraviolet, or UV, radiation comes from the sun and artificial sources such as tanning beds. It can damage skin cells and contribute to skin cancer. A cool breeze or cloud cover does not remove UV exposure, and rays can reflect from water, sand, concrete and other surfaces.",
          "The local UV Index describes the expected strength of UV radiation at the earth's surface. It can be a more useful planning tool than temperature alone. Exposure also depends on time of day, altitude, surroundings, duration and the protection being used.",
        ],
      },
      {
        heading: "Use layers instead of relying on one product",
        paragraphs: [
          "Shade, clothing that covers the skin, a wide-brimmed hat and sunglasses that block UVA and UVB rays can reduce exposure. Broad-spectrum sunscreen adds protection for exposed areas, but it is not a shield that makes unlimited sun exposure safe. Coverage, application amount, missed areas and reapplication all affect performance.",
          "Indoor tanning exposes skin to intense UV radiation and does not create a healthy or protective base tan. Any tan after UV exposure is a sign that the skin has responded to injury. Sun protection matters across skin tones, even though the likelihood and appearance of sunburn and skin cancer can differ.",
        ],
        bullets: [
          "Check the local UV Index when planning outdoor time.",
          "Combine shade, clothing, a hat, sunglasses and sunscreen.",
          "Remember ears, the back of the neck, hands and feet.",
          "Avoid tanning beds and sunlamps.",
        ],
      },
      {
        heading: "Notice change rather than trying to diagnose it",
        paragraphs: [
          "A new or changing mole, a spot that repeatedly bleeds, or a sore that does not heal should be shown to a healthcare professional. Many skin changes are not cancer, and photographs or checklists cannot replace an examination when a change is concerning.",
          "This guide is general education, not a skin-cancer diagnosis or an individualized sunscreen recommendation. Skin conditions, allergies, medicines, age and occupational exposure can change what is suitable. A qualified professional can assess a lesion and discuss personal risk.",
        ],
      },
    ],
  },
  {
    slug: "bone-strength-before-fracture",
    title: "Bone strength before the first fracture",
    excerpt:
      "Osteoporosis may remain unnoticed until a bone breaks. Understanding risk, testing and fall prevention can support a more useful conversation.",
    category: "Healthy living",
    readTime: "4 min read",
    published: "2026-08-01",
    imageTone: "lime",
    sourceLabel: "NIH/NIAMS — Bone Health and Osteoporosis",
    sourceUrl: "https://www.niams.nih.gov/health-topics/bone-health-and-osteoporosis",
    sections: [
      {
        heading: "Bone is living tissue",
        paragraphs: [
          "Bone is continuously broken down and rebuilt. Osteoporosis develops when bone mass, mineral density or structure changes enough to make bones weaker and more likely to fracture. It is often described as a silent disease because there may be no warning until a fracture occurs.",
          "Fractures related to fragile bones often affect the hip, spine or wrist. A fracture after a minor fall, unexplained loss of height or sudden back pain can prompt assessment. These signs have other possible causes, so they should be evaluated rather than used for self-diagnosis.",
        ],
      },
      {
        heading: "Risk comes from more than age",
        paragraphs: [
          "Risk increases with age and can rise around and after menopause, but osteoporosis also affects men and younger people. Family history, a previous low-trauma fracture, low body weight, long periods of inactivity, smoking, heavy alcohol use and some medical conditions can matter. Long-term use of medicines such as corticosteroids may also affect bone.",
          "A bone-density test can estimate bone strength and help inform fracture risk, but the result is only part of the picture. A clinician also considers age, previous fractures, health conditions, medicines and other risk factors when deciding whether testing or treatment is appropriate.",
        ],
        bullets: [
          "Mention fractures that followed a minor fall or bump.",
          "Review medicines that may affect bone or balance.",
          "Include family history of osteoporosis or hip fracture.",
          "Ask how a bone-density result changes the next decision.",
        ],
      },
      {
        heading: "Strong bones and fewer falls work together",
        paragraphs: [
          "Adequate calcium, vitamin D, protein and overall nutrition support bone health. Weight-bearing and resistance activity can support bone and muscle, while balance work and a safer home environment can reduce falls. Supplements and exercise intensity should reflect personal needs, kidney function, medicines and existing fracture risk.",
          "This article is educational only. It does not diagnose osteoporosis, recommend a supplement dose or provide a personal exercise or screening plan. A qualified healthcare professional can assess fracture risk, interpret testing and tailor prevention or treatment safely.",
        ],
      },
    ],
  },
  {
    slug: "stroke-warning-signs-be-fast",
    title: "Stroke warning signs: think BE FAST",
    excerpt:
      "Sudden changes in balance, vision, facial movement, arm strength or speech can signal a stroke. Learn the BE FAST check and why symptoms that disappear still need urgent action.",
    category: "Prevention",
    readTime: "5 min read",
    published: "2026-10-03",
    imageTone: "coral",
    sourceLabel: "Centers for Disease Control and Prevention — Stroke signs and symptoms",
    sourceUrl: "https://www.cdc.gov/stroke/signs-symptoms/index.html",
    sections: [
      {
        heading: "Stroke symptoms usually begin suddenly",
        paragraphs: [
          "A stroke interrupts blood flow in the brain or occurs when a blood vessel in the brain bursts. It is a medical emergency because brain injury can progress quickly. Warning signs often appear without warning and may affect movement, speech, vision, understanding or coordination.",
          "Possible signs include sudden weakness or numbness of the face, arm or leg—especially on one side—sudden confusion, trouble speaking, difficulty understanding speech, a new problem seeing, loss of balance or coordination, or a sudden severe headache with no known cause. A person does not need to have every sign for a stroke to be possible.",
        ],
      },
      {
        heading: "Use BE FAST as a quick check",
        paragraphs: [
          "BE FAST is a memory aid, not a home diagnostic test. It helps people notice several common stroke signs and move quickly toward emergency assessment. If even one part of the check is suddenly abnormal, do not wait for additional symptoms.",
          "Note when the person was last known to be well, or when the symptoms first appeared. That timing can help the hospital team make treatment decisions. Do not delay the emergency call while repeating the test or searching online.",
        ],
        bullets: [
          "B — Balance: sudden dizziness, loss of balance or poor coordination.",
          "E — Eyes: sudden loss of vision, double vision or another new visual change.",
          "F — Face: ask the person to smile and look for one-sided drooping.",
          "A — Arms: ask them to raise both arms and watch for weakness or downward drift.",
          "S — Speech: listen for slurred, unusual or difficult speech.",
          "T — Time: call your local emergency number immediately if a sign is present.",
        ],
      },
      {
        heading: "Act even if the symptoms improve",
        paragraphs: [
          "Call local emergency services immediately when stroke is suspected and follow the dispatcher's instructions. An ambulance team may begin assessment and care before arrival at hospital. Do not let the affected person drive, and do not delay the call to give food, drink or medicines unless emergency professionals direct you.",
          "Stroke-like symptoms that disappear after a few minutes can occur with a transient ischemic attack, sometimes called a TIA. Improvement does not make the episode safe to ignore; urgent medical assessment is still needed. This guide is general education and cannot determine the cause of an individual person's symptoms.",
        ],
      },
    ],
  },
  {
    slug: "heart-attack-warning-signs",
    title: "Heart attack warning signs are not always dramatic",
    excerpt:
      "A heart attack can begin with mild, changing or less familiar symptoms. Recognize the possible warning pattern and know why uncertainty should not delay emergency help.",
    category: "Prevention",
    readTime: "5 min read",
    published: "2026-10-03",
    imageTone: "coral",
    sourceLabel: "National Heart, Lung, and Blood Institute — Heart attack symptoms",
    sourceUrl: "https://www.nhlbi.nih.gov/health/heart-attack/symptoms",
    sections: [
      {
        heading: "Symptoms can be sudden, gradual or easy to dismiss",
        paragraphs: [
          "A heart attack happens when blood carrying oxygen to part of the heart muscle becomes blocked. Some attacks cause intense chest pain, but others begin slowly with mild pressure, heaviness or discomfort. Symptoms may come and go, and a person's second heart attack may not feel like an earlier one.",
          "Chest discomfort is the most common warning sign, yet it is not the only one. Older adults and people with diabetes may have very mild symptoms or a silent heart attack. Women can also experience chest discomfort, but unusual tiredness, nausea, shortness of breath or pain away from the chest may be prominent.",
        ],
      },
      {
        heading: "Know the possible warning pattern",
        paragraphs: [
          "No single symptom confirms a heart attack outside a medical assessment. The concern rises when symptoms are new, unexplained, occur together or feel different from a person's usual pattern. Do not rely on pain severity alone.",
          "Heartburn, anxiety, muscle strain and other conditions can resemble heart symptoms, but it is unsafe to make that distinction at home when a heart attack is possible. Emergency clinicians can use an examination, heart tracing and blood tests to investigate the cause.",
        ],
        bullets: [
          "Pain, pressure, heaviness or discomfort in the centre or left side of the chest.",
          "Discomfort in one or both arms, the shoulders, back, neck, jaw or upper abdomen.",
          "Shortness of breath at rest or with very little activity.",
          "Unexplained sweating, nausea, vomiting, light-headedness or sudden dizziness.",
          "Unusual tiredness, weakness, or a rapid or irregular heartbeat.",
        ],
      },
      {
        heading: "When in doubt, make the emergency call",
        paragraphs: [
          "If you think you or someone else may be having a heart attack, call your local emergency number immediately—even if you are not certain. Follow the dispatcher's instructions and do not delay the call while waiting for symptoms to settle or while trying food, antacids, aspirin or another remedy.",
          "An ambulance is generally the safest transport because trained professionals can assess the person and begin emergency care on the way. Do not drive yourself. This article cannot diagnose chest symptoms or replace individual emergency advice.",
        ],
      },
    ],
  },
  {
    slug: "sepsis-signs-act-fast",
    title: "Sepsis: know the signs and act fast",
    excerpt:
      "Sepsis is a life-threatening response to infection. Learn which combinations of worsening illness should raise concern and why rapid medical assessment matters.",
    category: "Prevention",
    readTime: "4 min read",
    published: "2026-10-03",
    imageTone: "blue",
    sourceLabel: "Centers for Disease Control and Prevention — About sepsis",
    sourceUrl: "https://www.cdc.gov/sepsis/about/index.html",
    sections: [
      {
        heading: "Sepsis begins with an infection",
        paragraphs: [
          "Sepsis is the body's extreme response to an infection and is a life-threatening medical emergency. The infection may begin in places such as the lungs, urinary tract, skin or digestive system. Bacterial infections cause many cases, but viral and fungal infections can also lead to sepsis.",
          "There is no reliable home test that can confirm or rule out sepsis. The important pattern is an infection—or possible infection—together with a person becoming seriously or rapidly unwell. Fast professional evaluation matters because sepsis can progress to tissue damage and organ failure.",
        ],
      },
      {
        heading: "Look for a concerning combination",
        paragraphs: [
          "A person with sepsis may have one or several warning signs, and not everyone will develop a fever. Pay attention to a marked change from the person's usual state, especially while an infection is worsening or not improving.",
          "These signs also occur in conditions other than sepsis, so the list is not a diagnostic checklist. A healthcare team must assess the whole situation, including vital signs, examination findings and tests.",
        ],
        bullets: [
          "Confusion, disorientation or an unusual change in alertness.",
          "Shortness of breath or visibly difficult breathing.",
          "Fever, shivering or feeling unusually cold.",
          "A high heart rate, weak pulse, or clammy and sweaty skin.",
          "Extreme pain, severe discomfort or a strong sense that something is seriously wrong.",
        ],
      },
      {
        heading: "Who needs particular awareness",
        paragraphs: [
          "Anyone can develop sepsis, but risk is higher in babies younger than one year, adults aged 65 or older, people with chronic illnesses or weakened immune systems, pregnant or recently postpartum people, and those with recent severe illness, surgery or hospital care. Previous sepsis also increases future risk.",
          "If someone with a possible infection is rapidly worsening, confused, very breathless, difficult to wake or otherwise severely unwell, call local emergency services and say that you are concerned about sepsis. For a less dramatic infection that is not improving, contact a healthcare professional promptly. This guide does not diagnose sepsis or replace emergency assessment.",
        ],
      },
    ],
  },
  {
    slug: "heartburn-reflux-gerd",
    title: "Heartburn, reflux and GERD: what the pattern can tell you",
    excerpt:
      "Occasional reflux is common, while persistent or complicated symptoms deserve a closer look. Learn what to track, which changes may help and when to seek care.",
    category: "Healthy living",
    readTime: "5 min read",
    published: "2026-10-03",
    imageTone: "lime",
    sourceLabel: "National Institute of Diabetes and Digestive and Kidney Diseases — GER and GERD",
    sourceUrl: "https://www.niddk.nih.gov/health-information/digestive-diseases/acid-reflux-ger-gerd-adults/symptoms-causes",
    sections: [
      {
        heading: "Reflux and GERD are related, but not identical",
        paragraphs: [
          "Gastroesophageal reflux happens when stomach contents move back into the esophagus. It may cause heartburn—a burning feeling behind the breastbone—or regurgitation, in which food or sour-tasting fluid reaches the throat or mouth. Occasional reflux is common.",
          "Gastroesophageal reflux disease, or GERD, is a longer-lasting condition in which reflux repeatedly causes bothersome symptoms or complications. Some people have nausea, swallowing problems, chronic cough or hoarseness rather than classic heartburn. A clinician considers the symptom pattern and health history before deciding whether tests are needed.",
        ],
      },
      {
        heading: "Track your pattern instead of banning every food",
        paragraphs: [
          "Triggers differ from person to person. Coffee, alcohol, chocolate, mint, acidic foods, spicy foods and high-fat meals are commonly reported, but a long restriction list is not automatically helpful. A short record of meals, symptom timing, sleep and medicines can reveal a more personal pattern.",
          "When symptoms occur at night, leaving about three hours between a meal and lying down may help some people. Other clinician-recommended measures can include stopping smoking, adjusting eating habits, raising the upper body during sleep, or pursuing gradual weight loss when medically appropriate. Do not stop prescribed medicine because it appears related without discussing it with the prescriber.",
        ],
        bullets: [
          "Record when burning, regurgitation, cough or nausea begins.",
          "Note meals, drinks, body position and medicines around that time.",
          "Look for a repeated pattern rather than blaming a single meal.",
          "Tell a clinician how often symptoms occur and whether they disturb sleep.",
        ],
      },
      {
        heading: "Know when symptoms need assessment",
        paragraphs: [
          "Arrange medical review when reflux is frequent, persistent, getting worse or not improving with appropriate over-the-counter treatment and lifestyle changes. Seek prompt care for painful or difficult swallowing, persistent vomiting, loss of appetite, unexplained weight loss, bloody vomit, vomit that resembles coffee grounds, or black or bloody stool.",
          "Chest pain should not automatically be labelled heartburn. New or unexplained chest pressure or pain—especially with breathlessness, sweating, nausea, dizziness, or discomfort in an arm, the back, neck or jaw—may require immediate emergency assessment. This guide is educational and does not diagnose GERD or recommend an individual medicine plan.",
        ],
      },
    ],
  },
  {
    slug: "headache-patterns-red-flags",
    title: "Headache patterns: what to track and when to act",
    excerpt:
      "Most headaches are not caused by a dangerous condition, but a sudden or changing pattern can matter. Learn what belongs in a headache diary and which signs need urgent help.",
    category: "Prevention",
    readTime: "5 min read",
    published: "2026-10-03",
    imageTone: "aqua",
    sourceLabel: "NHS — Headaches",
    sourceUrl: "https://www.nhs.uk/symptoms/headaches/",
    sections: [
      {
        heading: "The pattern is often more useful than one pain score",
        paragraphs: [
          "Headache is a symptom with many possible causes. Common primary headache disorders include tension-type headache and migraine, while a secondary headache is caused by another health problem. Location or intensity alone usually cannot identify the type.",
          "Migraine may involve nausea and sensitivity to light or sound, with or without aura. Tension-type headache is often described as pressure or tightness. These are general patterns, not a way to diagnose yourself; symptoms can overlap and a clinician may need to review new, frequent or disabling headaches.",
        ],
      },
      {
        heading: "Build a useful headache diary",
        paragraphs: [
          "A simple diary can help reveal frequency, changes and possible associations. Record information during or soon after an episode rather than relying on memory at a later appointment. Avoid assuming that every event before a headache is a trigger until a pattern repeats.",
          "Frequent use of pain-relief medicines can itself contribute to recurring headaches in some people. Record all medicines and doses honestly, including non-prescription products, and ask a clinician or pharmacist for advice rather than abruptly changing a long-used treatment.",
        ],
        bullets: [
          "Start time, duration, location and how the pain felt.",
          "Associated nausea, vomiting, light sensitivity, visual changes or other symptoms.",
          "Sleep, meals, hydration, menstrual timing, stress and recent illness.",
          "Medicines taken, the dose, timing and whether they helped.",
          "How the headache affected work, study, sleep or normal activity.",
        ],
      },
      {
        heading: "Red flags should change the plan",
        paragraphs: [
          "Call local emergency services for a headache that is sudden and extremely painful, or a severe headache with new weakness, numbness, facial droop, trouble speaking, confusion, fainting, seizure, loss of vision or difficulty walking. A severe headache after a significant head injury, or with high fever and a stiff neck, also needs emergency assessment.",
          "Seek timely medical review when headaches are becoming more frequent or severe, repeatedly wake you, follow a new pattern, do not improve, or occur with vomiting or concerning vision symptoms. New headaches during pregnancy or soon after birth also deserve prompt advice. This article cannot identify the cause of an individual headache.",
        ],
      },
    ],
  },
  {
    slug: "low-back-pain-recovery-red-flags",
    title: "Low back pain: support recovery and recognize red flags",
    excerpt:
      "Most episodes of low back pain improve, and gentle activity is often part of recovery. Learn why scans are not always the first step and which symptoms need urgent assessment.",
    category: "Healthy living",
    readTime: "5 min read",
    published: "2026-10-03",
    imageTone: "blue",
    sourceLabel: "World Health Organization — Low back pain",
    sourceUrl: "https://www.who.int/news-room/fact-sheets/detail/low-back-pain",
    sections: [
      {
        heading: "Pain does not always reveal a single damaged structure",
        paragraphs: [
          "Low back pain is very common and often improves within several weeks, although it can last longer or return. A muscle strain is one possible cause, but frequently there is no single obvious structure responsible. Sciatica describes pain or other symptoms related to irritation of a nerve travelling into a leg.",
          "A clinician may ask about the onset, function, leg symptoms, general health and any warning signs before examining movement, strength and sensation. Routine imaging is not recommended for every uncomplicated episode because a scan is most useful when its result is likely to change management.",
        ],
      },
      {
        heading: "Keep recovery practical and gradual",
        paragraphs: [
          "Guidance encourages people to continue normal activities as far as they reasonably can. That may mean temporarily changing pace, range or load rather than avoiding all movement. Walking, position changes and an appropriate exercise programme can support recovery, but the right starting point depends on symptoms and ability.",
          "A pharmacist, physiotherapist or clinician can help when pain limits daily life or when advice about medicines and activity is needed. Manual therapy, when appropriate, is generally considered as part of a broader programme that includes exercise rather than as a complete treatment on its own.",
        ],
        bullets: [
          "Use comfortable movement and build activity gradually.",
          "Change position regularly if prolonged sitting or standing aggravates symptoms.",
          "Track function—such as walking, sleep and daily tasks—not pain alone.",
          "Arrange review if pain persists for weeks, worsens or significantly limits daily activity.",
        ],
      },
      {
        heading: "Some symptoms need urgent assessment",
        paragraphs: [
          "Seek emergency help for new loss of feeling around the genitals or anus, new difficulty controlling or passing urine or stool, or severe back pain with new weakness, numbness or tingling in both legs. These can signal pressure on important spinal nerves and should not wait for a routine appointment.",
          "Urgent assessment is also appropriate after a serious accident, or when severe pain begins suddenly, worsens rapidly, or comes with fever, chills or feeling generally very unwell. Unexplained weight loss, pain that is worse at night, a new lump or a history that raises concern should be discussed with a clinician. This guide is general education, not an individual diagnosis or rehabilitation plan.",
        ],
      },
    ],
  },
];
